import { useState, Fragment, useMemo, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Save, Pencil, X, Plus, Shuffle, Ban, CheckCircle2, AlertTriangle, ArrowUp, ArrowDown, ChevronsUpDown, ListChecks } from 'lucide-react';
import { api } from '../services/api';
import LoadingSpinner from '../components/Shared/LoadingSpinner';
import PageHeader from '../components/Shared/PageHeader';
import { c, t, tbl, card, btn } from '../theme';

const GROUPS = ['Operations', 'Maintenance', 'Administration'];

// HR's four-way classification, and the one the Overtime board's category chart
// groups on. A profile is given a CATEGORY here and the labour class is derived
// from it server-side -- the class alone used to be all this form sent, which
// left job_category NULL on every profile added from this page and put its
// dollars in "Not in the mapping" on that chart no matter how well it was
// mapped everywhere else.
const CATEGORIES = ['Direct Labor', 'Indirect Labor', 'G&A Labor', 'Sales Labor'];
const classForCategory = (cat) => (cat === 'Direct Labor' ? 'direct' : 'indirect');

const BLANK_NEW = {
  job_profile: '', job_family: '', job_family_group: 'Operations',
  job_category: 'Direct Labor', labor_class_note: '',
};

const money = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const hours = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// The date the bible load and the two gap-fill migrations used for "before all
// loaded history". A mapping dated later than this is forward-only.
const FIRST_MAPPING_SENTINEL = '2025-01-01';

export default function JobProfileMapping() {
  const qc = useQueryClient();

  const [filters, setFilters] = useState({ q: '', group: '', class: '', confirmed: '' });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [reclassId, setReclassId] = useState(null);
  const [reclassForm, setReclassForm] = useState({ labor_class: 'direct', labor_class_note: '' });
  const [adding, setAdding] = useState(false);
  const [newForm, setNewForm] = useState(BLANK_NEW);

  // Sorting is client-side — the server's default order (group, family,
  // profile) already IS the grouped view, so a click-to-sort only replaces
  // that grouping with a flat order; clearing the sort returns to it.
  const [sort, setSort] = useState(null); // { key, dir: 'asc' | 'desc' }

  // Selection is keyed by row id, cleared whenever the filtered set changes
  // (a stale selection surviving a filter change is how a bulk action ends up
  // touching a row the user can no longer see).
  const [selected, setSelected] = useState(new Set());
  const [bulkAction, setBulkAction] = useState(null); // 'assign' | 'reclassify' | null
  const [bulkAssignForm, setBulkAssignForm] = useState({ job_family: '', job_family_group: 'Operations' });
  const [bulkReclassForm, setBulkReclassForm] = useState({ labor_class: 'direct', labor_class_note: '' });

  // Inline quick-add, keyed by the unmatched profile's own name — lets
  // reviewing the coverage gap and closing it happen in the same spot,
  // instead of jumping to the full Add panel and losing your place.
  const [quickAddFor, setQuickAddFor] = useState(null);
  const [quickAddForm, setQuickAddForm] = useState(BLANK_NEW);

  const params = useMemo(() => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) p.set(k, v); });
    const s = p.toString();
    return s ? `?${s}` : '';
  }, [filters]);

  // A selection surviving a filter change is how a bulk action ends up
  // touching a row the user can no longer see — clear it instead.
  useEffect(() => { setSelected(new Set()); }, [params]);

  const { data, isLoading } = useQuery({
    queryKey: ['job-profiles', params],
    queryFn:  () => api.get(`/job-profiles${params}`),
  });
  const { data: coverage } = useQuery({
    queryKey: ['job-profiles-coverage'],
    queryFn:  () => api.get('/job-profiles/coverage'),
  });
  const { data: families = [] } = useQuery({
    queryKey: ['job-profile-families'],
    queryFn:  () => api.get('/job-profiles/families'),
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['job-profiles'] });
    qc.invalidateQueries({ queryKey: ['job-profiles-coverage'] });
    qc.invalidateQueries({ queryKey: ['job-profile-families'] });
  };
  const fail = (err) => toast.error(err.message || 'That did not save.');

  const updateM = useMutation({
    mutationFn: ({ id, payload }) => api.patch(`/job-profiles/${id}`, payload),
    onSuccess: () => { toast.success('Profile updated.'); refresh(); setEditingId(null); },
    onError: fail,
  });
  const reclassM = useMutation({
    mutationFn: ({ id, payload }) => api.post(`/job-profiles/${id}/reclassify`, payload),
    onSuccess: (row) => {
      toast.success(`Reclassified as ${row.labor_class} from today. Earlier weeks keep their previous class.`);
      refresh(); setReclassId(null);
    },
    onError: fail,
  });
  const createM = useMutation({
    mutationFn: (payload) => api.post('/job-profiles', payload),
    onSuccess: () => { toast.success('Profile added.'); refresh(); setAdding(false); setNewForm(BLANK_NEW); },
    onError: fail,
  });
  const deactivateM = useMutation({
    mutationFn: (id) => api.delete(`/job-profiles/${id}`),
    onSuccess: (d) => { toast.success(`${d.job_profile} deactivated.`); refresh(); },
    onError: fail,
  });
  const confirmM = useMutation({
    mutationFn: (payload) => api.post('/job-profiles/confirm', payload),
    onSuccess: (d) => { toast.success(`${d.updated} classification(s) released.`); refresh(); },
    onError: fail,
  });
  const bulkAssignM = useMutation({
    mutationFn: (payload) => api.post('/job-profiles/bulk-assign', payload),
    onSuccess: (d) => {
      toast.success(`${d.updated} profile(s) reassigned.`);
      refresh(); setSelected(new Set()); setBulkAction(null);
    },
    onError: fail,
  });
  const bulkReclassifyM = useMutation({
    mutationFn: (payload) => api.post('/job-profiles/bulk-reclassify', payload),
    onSuccess: (d) => {
      toast.success(
        `${d.reclassified} reclassified` + (d.skipped ? `, ${d.skipped} already there` : '') + '.'
      );
      refresh(); setSelected(new Set()); setBulkAction(null);
    },
    onError: fail,
  });
  const bulkDeactivateM = useMutation({
    mutationFn: (ids) => api.post('/job-profiles/bulk-deactivate', { ids }),
    onSuccess: (d) => { toast.success(`${d.updated} profile(s) deactivated.`); refresh(); setSelected(new Set()); },
    onError: fail,
  });

  if (isLoading) return <LoadingSpinner />;

  const rows = data?.rows || [];
  const totals = data?.totals || {};
  const cov = coverage?.totals;
  const familyNames = [...new Set(families.map((f) => f.job_family))].sort();

  // Sorting is client-side. The server's default order (group, family,
  // profile) already reads as the grouped view; clicking a column replaces
  // that grouping with a flat order, and a third click clears back to it.
  const SORT_VALUE = {
    job_profile:      (r) => r.job_profile.toLowerCase(),
    job_family:       (r) => r.job_family.toLowerCase(),
    job_family_group: (r) => r.job_family_group.toLowerCase(),
    job_category:     (r) => r.job_category || '',
    labor_class:      (r) => r.labor_class,
    ot_amount:        (r) => Number(r.ot_amount || 0),
  };
  function toggleSort(key) {
    setSort((s) => {
      if (!s || s.key !== key) return { key, dir: 'asc' };
      if (s.dir === 'asc') return { key, dir: 'desc' };
      return null;
    });
  }
  const sortedRows = sort
    ? [...rows].sort((a, b) => {
        const va = SORT_VALUE[sort.key](a);
        const vb = SORT_VALUE[sort.key](b);
        const cmp = typeof va === 'number' ? va - vb : va.localeCompare(vb);
        return sort.dir === 'asc' ? cmp : -cmp;
      })
    : rows;

  // Without an active sort, insert a divider whenever the group or family
  // changes — the rows already arrive grouped, this just makes it visible.
  // An active sort linearizes the list, so no dividers apply.
  const displayItems = sort
    ? sortedRows.map((row) => ({ type: 'row', row }))
    : rows.reduce((acc, row, i) => {
        const prev = rows[i - 1];
        if (!prev || prev.job_family_group !== row.job_family_group || prev.job_family !== row.job_family) {
          acc.push({ type: 'divider', key: `${row.job_family_group}::${row.job_family}`, group: row.job_family_group, family: row.job_family });
        }
        acc.push({ type: 'row', row });
        return acc;
      }, []);

  const visibleIds = rows.map((r) => r.id);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
  function toggleSelectAll() {
    setSelected(allVisibleSelected ? new Set() : new Set(visibleIds));
  }
  function toggleSelectOne(id) {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function startEdit(row) {
    setReclassId(null);
    setEditingId(row.id);
    setEditForm({
      job_profile: row.job_profile,
      job_family: row.job_family,
      job_family_group: row.job_family_group,
      job_category: row.job_category,
      labor_class_note: row.labor_class_note || '',
      is_active: row.is_active,
    });
  }

  function startReclass(row) {
    setEditingId(null);
    setReclassId(row.id);
    setReclassForm({
      labor_class: row.labor_class === 'direct' ? 'indirect' : 'direct',
      labor_class_note: row.labor_class_note || '',
    });
  }

  return (
    <div style={s.page}>
      {/* Rendered once, unconditionally — a <datalist> only has to exist
          somewhere in the DOM to back every input that references it by id,
          but nested inside the Add panel it vanished whenever that panel was
          closed, silently turning autocomplete off for the quick-add, bulk
          assign, and inline edit forms. */}
      <datalist id="family-options">
        {familyNames.map((f) => <option key={f} value={f} />)}
      </datalist>
      <PageHeader
        eyebrow="Mappings"
        title="Job Profiles"
        subtitle="Every job profile mapped to a family, a group, and direct or indirect labor. This is the dimension the Overtime board groups by — a profile missing here drops out of every family rollup."
        meta={
          <>
            <span style={s.chip}>{totals.total} profiles</span>
            <span style={s.chip}>{totals.direct} direct · {totals.indirect} indirect</span>
            {totals.unconfirmed > 0 && (
              <span style={{ ...s.chip, ...s.chipWarn }}>{totals.unconfirmed} awaiting sign-off</span>
            )}
          </>
        }
        actions={
          <div style={s.headerActions}>
            {totals.unconfirmed > 0 && (
              <button
                style={btn.secondarySm}
                onClick={() => {
                  // What releasing actually does. It never gated reporting -- every
                  // figure on the Overtime board is already computed from this
                  // classification -- so promising visibility described a feature
                  // that did not exist. What it does control is whether the board
                  // tells its readers the classification is still awaiting sign-off.
                  if (window.confirm(
                    `Release ${totals.unconfirmed} drafted classification(s)?\n\n`
                    + 'The Overtime board stops flagging them as awaiting sign-off. '
                    + 'No figure changes — the board already reports from this '
                    + 'classification. This is the record that Operations and Finance '
                    + 'approved it, and it is logged against your name.'
                  )) {
                    confirmM.mutate({ all: true });
                  }
                }}
              >
                <CheckCircle2 size={14} /> Release all
              </button>
            )}
            <button style={btn.primarySm} onClick={() => { setAdding((v) => !v); setEditingId(null); setReclassId(null); }}>
              <Plus size={14} /> Add profile
            </button>
          </div>
        }
      />

      {/* Mapped, and classifying nothing. Distinct from the gap below: these
          profiles ARE in the mapping, so the coverage figure calls them covered
          and nothing here looks wrong -- but their mapping starts after the
          periods they were added to explain, so the board keeps their dollars in
          "Not in the mapping" and no amount of further mapping moves them. The
          fix is a dated backdating migration, not another entry on this page. */}
      {coverage?.not_effective_totals?.profiles > 0 && (
        <div style={s.coverage}>
          <div style={s.coverageTitle}>
            {coverage.not_effective_totals.profiles} mapped profile
            {coverage.not_effective_totals.profiles === 1 ? ' is' : 's are'} not classifying
            {' '}{money(coverage.not_effective_totals.ot_amount)} of overtime
          </div>
          <div style={s.coverageBody}>
            Their mapping starts after the periods these dollars are in, so the Overtime
            board still counts them under “Not in the mapping”. Adding them again will not
            help — the start date has to be moved by a migration.
          </div>
          <div style={s.coverageList}>
            {coverage.not_effective.slice(0, 8).map((u) => (
              <span key={u.job_profile} style={s.coverageItem}>
                {u.job_profile}
                <span style={s.coverageAmt}>{money(u.ot_amount)}</span>
                <span style={s.coverageAmt}>from {String(u.effective_from).slice(0, 10)}</span>
              </span>
            ))}
            {coverage.not_effective.length > 8 && (
              <span style={s.coverageMore}>+{coverage.not_effective.length - 8} more</span>
            )}
          </div>
        </div>
      )}

      {/* Drift signal. An unmatched profile silently shrinks a rollup, which
          reads as good news — so it is stated, not hidden behind a warning. */}
      {cov && cov.unmatched_profiles > 0 && (
        <div style={s.coverage}>
          <AlertTriangle size={16} style={{ color: c.warningText, flex: 'none', marginTop: '1px' }} />
          <div>
            <div style={s.coverageTitle}>
              {cov.unmatched_profiles} job profile{cov.unmatched_profiles === 1 ? '' : 's'} in payroll
              {' '}have no mapping — {money(cov.unmatched_amount)} of overtime unclassified
              {' '}({cov.pct_dollars_matched}% of dollars matched)
            </div>
            <div style={s.coverageList}>
              {(coverage.unmatched || []).slice(0, 8).map((u) => (
                <Fragment key={u.job_profile}>
                  <button
                    style={quickAddFor === u.job_profile ? { ...s.coverageItem, ...s.coverageItemOpen } : s.coverageItem}
                    title="Map this profile without leaving this list"
                    onClick={() => {
                      if (quickAddFor === u.job_profile) { setQuickAddFor(null); return; }
                      setQuickAddFor(u.job_profile);
                      setQuickAddForm({ ...BLANK_NEW, job_profile: u.job_profile });
                    }}
                  >
                    {u.job_profile} <span style={s.coverageAmt}>{money(u.ot_amount)}</span>
                  </button>
                  {quickAddFor === u.job_profile && (
                    <div style={s.quickAdd}>
                      <FamilyInput
                        id="jp-quick-family"
                        style={{ ...s.input, ...s.quickAddInput }}
                        placeholder="Job family"
                        value={quickAddForm.job_family}
                        onChange={(v) => setQuickAddForm({ ...quickAddForm, job_family: v })}
                        autoFocus
                      />
                      <select
                        style={{ ...s.input, ...s.quickAddInput }}
                        value={quickAddForm.job_family_group}
                        onChange={(e) => setQuickAddForm({ ...quickAddForm, job_family_group: e.target.value })}
                      >
                        {GROUPS.map((g) => <option key={g} value={g}>{g}</option>)}
                      </select>
                      <select
                        id="jp-quick-category"
                        style={{ ...s.input, ...s.quickAddInput }}
                        title="Labor category — the labour class follows from it"
                        value={quickAddForm.job_category}
                        onChange={(e) => setQuickAddForm({ ...quickAddForm, job_category: e.target.value })}
                      >
                        {CATEGORIES.map((cat) => <option key={cat} value={cat}>{cat}</option>)}
                      </select>
                      <button
                        id="jp-quick-add"
                        style={btn.primarySm}
                        disabled={createM.isPending || !quickAddForm.job_family}
                        onClick={() => createM.mutate(quickAddForm, { onSuccess: () => setQuickAddFor(null) })}
                      >
                        <Save size={13} /> Add
                      </button>
                      <button style={btn.icon} title="Cancel" onClick={() => setQuickAddFor(null)}><X size={14} /></button>
                    </div>
                  )}
                </Fragment>
              ))}
              {(coverage.unmatched || []).length > 8 && (
                <span style={s.coverageMore}>+{coverage.unmatched.length - 8} more</span>
              )}
            </div>
          </div>
        </div>
      )}

      {adding && (
        <div style={s.addPanel}>
          <div style={s.addGrid}>
            <Field label="Job profile (exact payroll spelling)">
              <input
                id="jp-new-profile"
                style={s.input}
                value={newForm.job_profile}
                onChange={(e) => setNewForm({ ...newForm, job_profile: e.target.value })}
                placeholder="e.g. Mechanic I - Compost"
              />
            </Field>
            <Field label="Job family">
              <FamilyInput
                id="jp-new-family"
                style={s.input}
                value={newForm.job_family}
                onChange={(v) => setNewForm({ ...newForm, job_family: v })}
              />
            </Field>
            <Field label="Group">
              <select style={s.input} value={newForm.job_family_group} onChange={(e) => setNewForm({ ...newForm, job_family_group: e.target.value })}>
                {GROUPS.map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
            </Field>
            <Field label="Labor category">
              <select id="jp-new-category" style={s.input} value={newForm.job_category} onChange={(e) => setNewForm({ ...newForm, job_category: e.target.value })}>
                {CATEGORIES.map((cat) => <option key={cat} value={cat}>{cat}</option>)}
              </select>
              <div style={s.hint}>
                Counts as <strong>{classForCategory(newForm.job_category)}</strong> labour, and appears
                under {newForm.job_category} on the Overtime category chart.
              </div>
            </Field>
          </div>
          <Field label="Note (why, if the call is contested)">
            <input style={s.input} value={newForm.labor_class_note} onChange={(e) => setNewForm({ ...newForm, labor_class_note: e.target.value })} />
          </Field>
          <div style={s.editActions}>
            <button style={btn.secondarySm} onClick={() => { setAdding(false); setNewForm(BLANK_NEW); }}>Cancel</button>
            <button style={btn.primarySm} disabled={createM.isPending} onClick={() => createM.mutate(newForm)}>
              <Save size={14} /> Add profile
            </button>
          </div>
        </div>
      )}

      <div style={s.filters}>
        <input
          style={{ ...s.input, maxWidth: '260px' }}
          placeholder="Search profile or family…"
          value={filters.q}
          onChange={(e) => setFilters({ ...filters, q: e.target.value })}
        />
        <select style={s.select} value={filters.group} onChange={(e) => setFilters({ ...filters, group: e.target.value })}>
          <option value="">All groups</option>
          {GROUPS.map((g) => <option key={g} value={g}>{g}</option>)}
        </select>
        <select style={s.select} value={filters.class} onChange={(e) => setFilters({ ...filters, class: e.target.value })}>
          <option value="">Direct and indirect</option>
          <option value="direct">Direct only</option>
          <option value="indirect">Indirect only</option>
        </select>
        <select style={s.select} value={filters.confirmed} onChange={(e) => setFilters({ ...filters, confirmed: e.target.value })}>
          <option value="">Any status</option>
          <option value="false">Awaiting sign-off</option>
          <option value="true">Released</option>
        </select>
        <span style={s.filterCount}>{rows.length} shown</span>
      </div>

      {selected.size > 0 && (
        <div style={s.bulkBar}>
          <ListChecks size={15} style={{ color: c.textMuted, flex: 'none' }} />
          <span style={s.bulkCount}>{selected.size} selected</span>
          <button style={btn.secondarySm} onClick={() => { setBulkAction((a) => a === 'assign' ? null : 'assign'); }}>
            Assign family/group
          </button>
          <button style={btn.secondarySm} onClick={() => { setBulkAction((a) => a === 'reclassify' ? null : 'reclassify'); }}>
            <Shuffle size={13} /> Reclassify
          </button>
          <button
            style={btn.secondarySm}
            onClick={() => {
              if (window.confirm(`Deactivate ${selected.size} profile(s)? They stop matching new payroll rows.`)) {
                bulkDeactivateM.mutate([...selected]);
              }
            }}
          >
            <Ban size={13} /> Deactivate
          </button>
          <button style={btn.linkXs} onClick={() => { setSelected(new Set()); setBulkAction(null); }}>Clear</button>

          {bulkAction === 'assign' && (
            <div style={s.bulkForm}>
              <FamilyInput
                style={{ ...s.input, ...s.quickAddInput }} placeholder="Job family"
                value={bulkAssignForm.job_family}
                onChange={(v) => setBulkAssignForm({ ...bulkAssignForm, job_family: v })}
              />
              <select
                style={{ ...s.input, ...s.quickAddInput }} value={bulkAssignForm.job_family_group}
                onChange={(e) => setBulkAssignForm({ ...bulkAssignForm, job_family_group: e.target.value })}
              >
                {GROUPS.map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
              <button
                style={btn.primarySm}
                disabled={bulkAssignM.isPending || !bulkAssignForm.job_family}
                onClick={() => bulkAssignM.mutate({ ids: [...selected], ...bulkAssignForm })}
              >
                <Save size={13} /> Apply to {selected.size}
              </button>
            </div>
          )}
          {bulkAction === 'reclassify' && (
            <div style={s.bulkForm}>
              <select
                style={{ ...s.input, ...s.quickAddInput }} value={bulkReclassForm.labor_class}
                onChange={(e) => setBulkReclassForm({ ...bulkReclassForm, labor_class: e.target.value })}
              >
                <option value="direct">Direct</option>
                <option value="indirect">Indirect</option>
              </select>
              <input
                style={{ ...s.input, ...s.quickAddInput, flex: '0 1 220px' }} placeholder="Reason"
                value={bulkReclassForm.labor_class_note}
                onChange={(e) => setBulkReclassForm({ ...bulkReclassForm, labor_class_note: e.target.value })}
              />
              <button
                style={btn.primarySm}
                disabled={bulkReclassifyM.isPending}
                onClick={() => bulkReclassifyM.mutate({ ids: [...selected], ...bulkReclassForm })}
              >
                <Shuffle size={13} /> Apply to {selected.size}
              </button>
            </div>
          )}
        </div>
      )}

      <div style={s.card}>
        <div style={{ overflowX: 'auto' }}>
          {/* Wide table: it scrolls inside this, so the page does not. */}
        <table style={s.table}>
          <thead>
            <tr>
              <th style={{ ...s.th, width: '32px' }}>
                <input type="checkbox" checked={allVisibleSelected} onChange={toggleSelectAll} />
              </th>
              {[
                ['job_profile', 'Job profile'],
                ['job_family', 'Family'],
                ['job_family_group', 'Group'],
                ['job_category', 'Category'],
                ['labor_class', 'Class'],
                ['ot_amount', 'Overtime'],
              ].map(([key, label]) => (
                <th key={key} style={key === 'ot_amount' ? { ...s.th, textAlign: 'right' } : s.th}>
                  <button style={s.sortBtn} onClick={() => toggleSort(key)}>
                    {label}
                    {sort?.key === key
                      ? (sort.dir === 'asc' ? <ArrowUp size={11} /> : <ArrowDown size={11} />)
                      : <ChevronsUpDown size={11} style={{ opacity: 0.35 }} />}
                  </button>
                </th>
              ))}
              <th style={s.th}></th>
            </tr>
          </thead>
          <tbody>
            {displayItems.map((item) => item.type === 'divider' ? (
              <tr key={item.key}>
                <td colSpan={8} style={s.groupDivider}>
                  {item.group} <span style={s.groupDividerSep}>›</span> {item.family}
                </td>
              </tr>
            ) : (
              <Fragment key={item.row.id}>
                {(() => { const row = item.row; return (
                <tr style={row.is_active ? s.tr : { ...s.tr, opacity: 0.55 }}>
                  <td style={s.td}>
                    <input type="checkbox" checked={selected.has(row.id)} onChange={() => toggleSelectOne(row.id)} />
                  </td>
                  <td style={s.td}>
                    <span style={s.profileName}>{row.job_profile}</span>
                    {!row.is_active && <span style={s.inactiveTag}>inactive</span>}
                    {/* The date that decides whether this mapping counts, shown
                        only when it is not the sentinel -- i.e. only when the
                        mapping is forward-only and the periods it was probably
                        added to explain are NOT classified by it. This page and
                        /coverage both ignore effective_from, and the board is
                        the only surface that tests it, so a profile could read
                        as fully mapped here while its dollars sat in "Not in the
                        mapping" over there with nothing on any screen to explain
                        the difference. */}
                    {row.effective_from && String(row.effective_from).slice(0, 10) > FIRST_MAPPING_SENTINEL && (
                      <div style={s.forwardOnly}>
                        Classifies payroll from {String(row.effective_from).slice(0, 10)} onward —
                        periods ending before that stay in “Not in the mapping”.
                      </div>
                    )}
                    {row.labor_class_note && <div style={s.note}>{row.labor_class_note}</div>}
                  </td>
                  <td style={s.td}>{row.job_family}</td>
                  <td style={s.td}>{row.job_family_group}</td>
                  {/* The column the Overtime category chart groups on. It was
                      not on this page at all, which is why a profile could read
                      as fully mapped here while the chart counted it under
                      "Not in the mapping" -- nothing on screen showed the field
                      that decided it. */}
                  <td style={s.td}>{row.job_category}</td>
                  <td style={s.td}>
                    <span style={row.labor_class === 'direct' ? s.pillDirect : s.pillIndirect}>
                      {row.labor_class}
                    </span>
                    {!row.labor_class_confirmed && <span style={s.draftTag}>draft</span>}
                  </td>
                  <td style={{ ...s.td, ...s.num }}>
                    {Number(row.ot_amount) > 0
                      ? <><div>{money(row.ot_amount)}</div><div style={s.subNum}>{hours(row.ot_hours)} hrs</div></>
                      : <span style={s.noneNum}>—</span>}
                  </td>
                  <td style={{ ...s.td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button style={btn.icon} title="Edit" onClick={() => startEdit(row)}><Pencil size={14} /></button>
                    <button style={btn.icon} title="Reclassify" onClick={() => startReclass(row)}><Shuffle size={14} /></button>
                    {row.is_active && (
                      <button
                        style={btn.icon}
                        title="Deactivate"
                        onClick={() => {
                          if (window.confirm(`Deactivate "${row.job_profile}"? It stops matching new payroll rows.`)) {
                            deactivateM.mutate(row.id);
                          }
                        }}
                      ><Ban size={14} /></button>
                    )}
                  </td>
                </tr>
                ); })()}

                {editingId === item.row.id && (
                  <tr style={s.editRow}>
                    <td colSpan={8} style={s.editTd}>
                      <div style={s.editGrid}>
                        <Field label="Job profile">
                          <input style={s.input} value={editForm.job_profile} onChange={(e) => setEditForm({ ...editForm, job_profile: e.target.value })} />
                        </Field>
                        <Field label="Job family">
                          <FamilyInput style={s.input} value={editForm.job_family} onChange={(v) => setEditForm({ ...editForm, job_family: v })} />
                        </Field>
                        <Field label="Group">
                          <select style={s.input} value={editForm.job_family_group} onChange={(e) => setEditForm({ ...editForm, job_family_group: e.target.value })}>
                            {GROUPS.map((g) => <option key={g} value={g}>{g}</option>)}
                          </select>
                        </Field>
                        {/* Only the categories that roll up to this row's
                            existing class. Indirect, G&A and Sales are three
                            ways of saying indirect, so moving between them
                            restates no class anyone was shown; crossing to or
                            from Direct Labor would change the class, and that
                            is effective-dated -- the reclassify action, which
                            now carries the category with it. */}
                        <Field label="Labor category">
                          <select style={s.input} value={editForm.job_category} onChange={(e) => setEditForm({ ...editForm, job_category: e.target.value })}>
                            {CATEGORIES.filter((cat) => classForCategory(cat) === item.row.labor_class)
                              .map((cat) => <option key={cat} value={cat}>{cat}</option>)}
                          </select>
                        </Field>
                        <Field label="Active">
                          <select style={s.input} value={editForm.is_active ? 'yes' : 'no'} onChange={(e) => setEditForm({ ...editForm, is_active: e.target.value === 'yes' })}>
                            <option value="yes">Active</option>
                            <option value="no">Inactive</option>
                          </select>
                        </Field>
                      </div>
                      <Field label="Note">
                        <input style={s.input} value={editForm.labor_class_note} onChange={(e) => setEditForm({ ...editForm, labor_class_note: e.target.value })} />
                      </Field>
                      <div style={s.hint}>Labor class is changed with the reclassify action, so earlier weeks keep the class they were reported under — and the category moves with it.</div>
                      <div style={s.editActions}>
                        <button style={btn.secondarySm} onClick={() => setEditingId(null)}><X size={14} /> Cancel</button>
                        <button style={btn.primarySm} disabled={updateM.isPending} onClick={() => updateM.mutate({ id: item.row.id, payload: editForm })}>
                          <Save size={14} /> Save
                        </button>
                      </div>
                    </td>
                  </tr>
                )}

                {reclassId === item.row.id && (
                  <tr style={s.editRow}>
                    <td colSpan={8} style={s.editTd}>
                      <div style={s.reclassHead}>
                        Reclassify <strong>{item.row.job_profile}</strong> from <em>{item.row.labor_class}</em>
                      </div>
                      <div style={s.editGrid}>
                        <Field label="New class">
                          <select style={s.input} value={reclassForm.labor_class} onChange={(e) => setReclassForm({ ...reclassForm, labor_class: e.target.value })}>
                            <option value="direct">Direct</option>
                            <option value="indirect">Indirect</option>
                          </select>
                        </Field>
                        <Field label="Reason">
                          <input style={s.input} value={reclassForm.labor_class_note} onChange={(e) => setReclassForm({ ...reclassForm, labor_class_note: e.target.value })} placeholder="Who decided, and on what basis" />
                        </Field>
                      </div>
                      <div style={s.hint}>
                        Takes effect today. The existing row is closed rather than overwritten, so weeks already reported keep their current class.
                        {' '}The labor category follows the class: to direct it becomes Direct Labor, and away from it,
                        {' '}Indirect Labor unless this profile is already G&amp;A or Sales.
                      </div>
                      <div style={s.editActions}>
                        <button style={btn.secondarySm} onClick={() => setReclassId(null)}><X size={14} /> Cancel</button>
                        <button style={btn.primarySm} disabled={reclassM.isPending} onClick={() => reclassM.mutate({ id: item.row.id, payload: reclassForm })}>
                          <Shuffle size={14} /> Reclassify
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
            {!rows.length && (
              <tr><td colSpan={7} style={s.empty}>No profiles match these filters.</td></tr>
            )}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label style={s.label}>{label}</label>
      {children}
    </div>
  );
}

// A native <datalist> filters its suggestions against whatever is already
// typed, so once a field holds a value, its own dropdown shows only itself —
// there is no browser-native way to "see everything" without emptying the
// field first. Clearing on focus surfaces the full list immediately; if the
// field is blurred still empty (looked, didn't pick anything), the original
// value comes back rather than leaving the field blank.
function FamilyInput({ value, onChange, style, placeholder, autoFocus, id }) {
  const beforeRef = useRef(value);
  return (
    <input
      id={id}
      style={style}
      list="family-options"
      placeholder={placeholder}
      autoFocus={autoFocus}
      value={value}
      onFocus={() => {
        beforeRef.current = value;
        if (value) onChange('');
      }}
      onBlur={() => { if (!value) onChange(beforeRef.current); }}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

const s = {
  page:        { maxWidth: '1180px', margin: '0 auto' },
  headerActions: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
  chip:        { ...t.mono, fontSize: '11px', color: c.textMuted, background: c.surfaceSunk, border: `1px solid ${c.border}`, borderRadius: '10px', padding: '2px 9px' },
  chipWarn:    { color: c.warningText, background: c.warningTint, borderColor: '#FCD34D' },

  coverage:    { display: 'flex', gap: '10px', alignItems: 'flex-start', background: c.warningTint, border: '1px solid #FCD34D', borderRadius: '8px', padding: '12px 14px', marginBottom: '14px' },
  coverageBody:  { fontSize: '12px', color: c.textMuted, marginBottom: '7px', lineHeight: 1.45 },
  coverageTitle: { fontSize: '13px', fontWeight: 600, color: c.warningText, marginBottom: '7px' },
  coverageList: { display: 'flex', flexWrap: 'wrap', gap: '6px' },
  coverageItem: { fontSize: '11px', color: c.warningText, background: c.surface, border: '1px solid #FCD34D', borderRadius: '10px', padding: '3px 9px', cursor: 'pointer', fontFamily: 'inherit' },
  coverageAmt:  { color: c.textMuted, marginLeft: '4px' },
  coverageMore: { fontSize: '11px', color: c.warningText, alignSelf: 'center' },
  coverageItemOpen: { borderColor: c.warningText, background: c.surfaceSunk },

  // Inline quick-add — sits directly under the coverage chip it belongs to,
  // full-width within the flex-wrap coverage list so it reads as attached to
  // that one profile rather than a second, disconnected panel.
  quickAdd:     { display: 'flex', gap: '6px', alignItems: 'center', flexBasis: '100%', marginTop: '2px' },
  quickAddInput:{ width: 'auto', flex: '0 1 150px' },

  addPanel:    { ...card.base, padding: '16px', marginBottom: '14px', display: 'flex', flexDirection: 'column', gap: '12px' },
  addGrid:     { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' },

  filters:     { display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap' },
  select:      { padding: '7px 10px', border: `1px solid ${c.borderStrong}`, borderRadius: '6px', fontSize: '12px', color: c.textStrong, fontFamily: 'inherit', background: c.surface },
  filterCount: { fontSize: '11px', color: c.textFaint, marginLeft: 'auto' },

  bulkBar:     { ...card.base, display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', padding: '10px 14px', marginBottom: '12px', background: c.surfaceSunk },
  bulkCount:   { fontSize: '12px', fontWeight: 600, color: c.textStrong, marginRight: '2px' },
  bulkForm:    { display: 'flex', gap: '8px', alignItems: 'center', flexBasis: '100%', marginTop: '4px' },

  card:        { ...card.base, overflow: 'hidden' },
  table:       { width: '100%', borderCollapse: 'collapse', fontSize: '12px' },
  th:          { ...tbl.th },
  sortBtn:     { display: 'inline-flex', alignItems: 'center', gap: '4px', border: 'none', background: 'none', padding: 0, font: 'inherit', color: 'inherit', cursor: 'pointer' },
  tr:          { ...tbl.tr },
  td:          { ...tbl.td, verticalAlign: 'top' },
  num:         { textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' },
  subNum:      { fontSize: '10px', color: c.textFaint },
  noneNum:     { color: c.textFaint },
  groupDivider:    { ...t.mono, fontSize: '10.5px', fontWeight: 600, color: c.textMuted, textTransform: 'uppercase', letterSpacing: '0.4px', background: c.surfaceSunk, padding: '6px 14px', borderBottom: `1px solid ${c.border}`, borderTop: `1px solid ${c.border}` },
  groupDividerSep: { color: c.textFaint, margin: '0 2px' },

  profileName: { fontWeight: 500, color: c.text },
  note:        { fontSize: '10.5px', color: c.textMuted, marginTop: '3px', maxWidth: '360px' },
  inactiveTag: { ...t.mono, fontSize: '9.5px', color: c.textMuted, background: c.canvas, borderRadius: '8px', padding: '1px 6px', marginLeft: '6px' },
  forwardOnly: { fontSize: '11px', color: c.warningText, marginTop: '3px', lineHeight: 1.35 },
  draftTag:    { ...t.mono, fontSize: '9.5px', color: c.warningText, background: c.warningTint, borderRadius: '8px', padding: '1px 6px', marginLeft: '6px' },
  pillDirect:  { ...t.mono, fontSize: '10px', fontWeight: 600, color: c.successText, background: c.successTint, borderRadius: '10px', padding: '2px 8px', textTransform: 'uppercase' },
  pillIndirect:{ ...t.mono, fontSize: '10px', fontWeight: 600, color: c.textMuted, background: c.canvas, borderRadius: '10px', padding: '2px 8px', textTransform: 'uppercase' },

  editRow:     { background: c.surfaceSunk, borderBottom: `1px solid ${c.border}` },
  editTd:      { padding: '16px' },
  editGrid:    { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', marginBottom: '12px' },
  reclassHead: { fontSize: '13px', color: c.textStrong, marginBottom: '12px' },
  label:       { display: 'block', fontSize: '11px', fontWeight: 600, color: c.textMuted, marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.5px' },
  input:       { width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: `1px solid ${c.borderStrong}`, borderRadius: '6px', fontSize: '13px', color: c.textStrong, fontFamily: 'inherit', background: c.surface },
  hint:        { fontSize: '11px', color: c.textMuted, margin: '10px 0' },
  editActions: { display: 'flex', justifyContent: 'flex-end', gap: '8px' },
  empty:       { textAlign: 'center', padding: '32px', color: c.textFaint },
};
